// What differs between the lists the document screens are mounted beside (Me's
// own documents, or a host's: cloud's documents of a workspace), besides the
// paths (place.tsx): how the list's "new document" and "new index" are made,
// what an index of the list offers to put in, what the document's header
// shows after its title and what its "…" adds. The screens know no list; they
// ask this.
//
// Promise: without a host's home the screens are Me's (the defaults below).
import { type UseQueryResult, useQuery } from "@tanstack/react-query";
import type { PageDTO } from "@trameria/core/documents-shared";
import {
  type ComponentType,
  createContext,
  type ReactNode,
  useContext,
} from "react";
import { useApi } from "../app/context";
import type { MenuItem } from "../ui/menu";
import { useCreateIndex } from "./indexes/queries";
import {
  type LinkCandidate,
  ownCandidatesQuery,
  useCreateDocument,
} from "./queries";

/** A write the screens start and watch (a TanStack mutation fits it). */
export interface DocumentsWrite<I, R> {
  mutate(
    input: I,
    options?: {
      onSuccess?: (result: R) => unknown;
      onError?: (error: Error) => unknown;
    },
  ): void;
  isPending: boolean;
  isError: boolean;
  error: Error | null;
}

export interface DocumentsHome {
  /** Hook: makes the document of the list's "new document" (its /new). */
  useCreateDocument(): DocumentsWrite<{ title: string }, { page: PageDTO }>;
  /** Hook: makes the index of the list's "new index". */
  useCreateIndex(): DocumentsWrite<{ title: string }, { key: string }>;
  /**
   * Hook: the documents an index of this list may be given, for what was typed
   * ("put a document already made"). Indexes among them are left out by the
   * screen.
   */
  useCandidates(query: string): UseQueryResult<LinkCandidate[]>;
  /**
   * Drawn right after the document's title in its header (where it is); for
   * a new document (`page` null), where it will be made. `narrow`: the header
   * is narrower than `DETAIL_HEADER_NARROW` (the title comes first).
   */
  HeaderStart?: ComponentType<{ page: PageDTO | null; narrow: boolean }>;
  /**
   * Hook: what this list adds to an open document's "…" (cloud: moving it to
   * another space, pinning it there), and what those items open. Unlike the
   * shell's header actions (one fixed item each, for every document), these
   * are the list's own and may follow state (pinned or not).
   */
  useDocumentMenu(page: PageDTO, writable: boolean): DocumentMenuAddition;
}

export interface DocumentMenuAddition {
  items: Array<MenuItem | "separator">;
  /** What the items open (a dialog), drawn beside the menu. */
  dialogs?: ReactNode;
}

function useMeCandidates(query: string) {
  return useQuery(ownCandidatesQuery(useApi(), query));
}

const NOTHING: DocumentMenuAddition = { items: [] };

/** Me's own documents. */
export const ME_HOME: DocumentsHome = {
  useCreateDocument,
  useCreateIndex,
  useCandidates: useMeCandidates,
  useDocumentMenu: () => NOTHING,
};

export const DocumentsHomeContext = createContext<DocumentsHome>(ME_HOME);

export const useDocumentsHome = () => useContext(DocumentsHomeContext);
